import {
  BLOG_LANGUAGES,
  BlogArticleModel,
  BlogArticleTranslation,
  BlogLanguage,
  BlogSectionModel,
} from '../../models/BlogArticle';

const WORDS_PER_MINUTE = 220;

export const toBlogLanguage = (language: string): BlogLanguage | undefined =>
  (BLOG_LANGUAGES as readonly string[]).includes(language)
    ? (language as BlogLanguage)
    : undefined;

const stripHtml = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getSectionText = (section: BlogSectionModel): string =>
  stripHtml(
    [section.sectionTitle, section.text, section.centeredText, section.imageDescription]
      .filter(Boolean)
      .join(' ')
  );

const mergeTranslatedSections = (
  baseSections: BlogSectionModel[],
  translatedSections?: Array<Partial<BlogSectionModel>>
): BlogSectionModel[] => {
  if (!translatedSections) return baseSections;
  return baseSections.map((section, index) => ({
    ...section,
    ...(translatedSections[index] || {}),
  }));
};

export const getLocalizedArticle = (
  article: BlogArticleModel,
  language?: BlogLanguage
): BlogArticleModel => {
  if (!language) return article;
  const translation = article.translations?.[language] as
    | BlogArticleTranslation
    | undefined;
  if (!translation) return article;

  return {
    ...article,
    title: translation.title ?? article.title,
    introduction: translation.introduction ?? article.introduction,
    text: mergeTranslatedSections(article.text, translation.text),
    sources: translation.sources ?? article.sources,
  };
};

export const getArticlePlainText = (
  article: BlogArticleModel,
  language?: BlogLanguage
): string => {
  const localizedArticle = getLocalizedArticle(article, language);

  return stripHtml(
    [
      localizedArticle.title,
      localizedArticle.introduction,
      localizedArticle.sources?.join(' ') || '',
      localizedArticle.text.map((section) => getSectionText(section)).join(' '),
    ].join(' ')
  );
};

export const estimateReadTimeMinutes = (
  article: BlogArticleModel,
  language?: BlogLanguage
): number => {
  const words = getArticlePlainText(article, language).split(' ').filter(Boolean).length;
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
};

export const getArticleExcerpt = (
  article: BlogArticleModel,
  language?: BlogLanguage,
  maxLength = 180
): string => {
  const localizedArticle = getLocalizedArticle(article, language);
  const introduction = stripHtml(localizedArticle.introduction);
  const fallback = stripHtml(
    localizedArticle.text
      .map((section) => section.text || section.centeredText || section.sectionTitle || '')
      .join(' ')
  );
  const fullText = introduction || fallback;

  if (!fullText) {
    return '';
  }

  if (fullText.length <= maxLength) {
    return fullText;
  }

  return `${fullText.slice(0, maxLength).trim()}...`;
};
